import React, { useEffect, useState } from 'react'
import { Image, Platform, Alert, Dimensions, Switch } from 'react-native'
import { ArrowLeft, Check, X, Camera } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { getProductSuggestions } from '../lib/openrouter'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import * as ImagePicker from 'expo-image-picker'
import AsyncStorage from '@react-native-async-storage/async-storage'
import {
  Button, ButtonText, EmptyState, Spinner, Text,
  XStack, YStack, ZStack, ScrollView,
} from '@fliisi/pollen'
import { FullScreenLayout } from '../components/ScreenLayout'

const { width } = Dimensions.get('window')
const isDesktop = width > 768

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'BulkUpload'>
  route: RouteProp<RootStackParamList, 'BulkUpload'>
}

interface UploadedProduct {
  id: string
  localUri: string
  status: 'uploading' | 'analyzing' | 'success' | 'error'
  title?: string
  price?: number
  error?: string
}

interface Reservation {
  id: string
  space_name: string
  market_name: string
  start_date: string
  end_date: string
}

export default function BulkUploadScreen({ navigation, route }: Props) {
  const { reservationId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const [products, setProducts] = useState<UploadedProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [autoLabel, setAutoLabel] = useState(true)
  const [batchId, setBatchId] = useState<string | null>(null)

  useEffect(() => {
    AsyncStorage.getItem('autoLabel').then((value) => {
      if (value !== null) setAutoLabel(value === 'true')
    })
  }, [])

  const toggleAutoLabel = async (value: boolean) => {
    setAutoLabel(value)
    await AsyncStorage.setItem('autoLabel', value.toString())
  }

  useEffect(() => {
    startBatch()
    fetchReservation()
  }, [])

  const startBatch = async () => {
    try {
      const { data, error } = await supabase.rpc('create_upload_batch', {
        p_seller_id: user?.id, p_market_id: null })
      if (error) throw error
      setBatchId(data)
    } catch (error) {
      console.error('Error creating batch:', error)
    }
  }

  const fetchReservation = async () => {
    try {
      const { data: resData, error: resError } = await supabase
        .from('space_reservations')
        .select('id, start_date, end_date, space_id, flea_market_id')
        .eq('id', reservationId)
        .single()
      if (resError) throw resError

      let spaceName = ''
      if (resData.space_id) {
        const { data: spaceData } = await supabase.from('market_spaces').select('name').eq('id', resData.space_id).single()
        spaceName = spaceData?.name || ''
      }
      let marketName = ''
      if (resData.flea_market_id) {
        const { data: marketData } = await supabase.from('flea_markets').select('name').eq('id', resData.flea_market_id).single()
        marketName = marketData?.name || ''
      }
      setReservation({ id: resData.id, space_name: spaceName, market_name: marketName, start_date: resData.start_date, end_date: resData.end_date })
    } catch (error) {
      console.error('Error fetching reservation:', error)
      const msg = t('reservation_not_found') || 'Reservation not found'
      if (Platform.OS === 'web') { window.alert(msg) } else { Alert.alert(t('error'), msg) }
      navigation.goBack()
    } finally {
      setLoading(false)
    }
  }

  const pickImages = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input')
      input.type = 'file'; input.accept = 'image/*'; input.multiple = true
      input.onchange = (e) => {
        const target = e.target as HTMLInputElement
        if (target.files && target.files.length > 0) processWebFiles(target.files)
      }
      input.click()
      return
    }
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync()
    if (status !== 'granted') {
      Alert.alert(t('permission_required') || 'Permission Required', t('gallery_permission_message') || 'Please allow access to your photo library to add images.')
      return
    }
    try {
      const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ["images"], allowsMultipleSelection: true, quality: 0.8 })
      if (!result.canceled && result.assets?.length) {
        for (const asset of result.assets) uploadAndProcessImage(asset.uri)
      }
    } catch (error) {
      console.error('Error picking images:', error)
      Alert.alert(t('error'), t('failed_to_pick_images') || 'Failed to pick images')
    }
  }

  const processWebFiles = async (files: FileList) => {
    for (const file of Array.from(files)) {
      const reader = new FileReader()
      reader.onload = (e) => { const dataUrl = e.target?.result as string; if (dataUrl) uploadAndProcessImage(dataUrl) }
      reader.readAsDataURL(file)
    }
  }

  const uploadAndProcessImage = async (uri: string) => {
    const localId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    setProducts((prev) => [...prev, { id: localId, localUri: uri, status: 'uploading' }])

    try {
      const { data: productData, error: productError } = await supabase.rpc('create_product_from_upload', { p_seller_id: user?.id, p_batch_id: batchId })
      if (productError) throw productError
      const row = Array.isArray(productData) ? productData[0] : productData
      const productId = row.product_id
      const imageId = row.image_id
      setProducts((prev) => prev.map((p) => (p.id === localId ? { ...p, id: productId } : p)))

      const response = await fetch(uri)
      const blob = await response.blob()
      const fileName = `${user?.id}/${productId}/${imageId}.jpg`
      const { error: uploadError } = await supabase.storage.from('product-images').upload(fileName, blob, { contentType: 'image/jpeg', upsert: true })
      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(fileName)
      const publicUrl = urlData.publicUrl
      await supabase.from('product_images').update({ image_url: publicUrl, upload_status: 'completed' }).eq('id', imageId)

      if (autoLabel) {
        setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, status: 'analyzing' } : p)))
        try {
          const suggestion = await getProductSuggestions(publicUrl)
          const barcode = `P${Date.now().toString(36).toUpperCase()}`
          await supabase.from('products').update({
            title: suggestion.title_en || '', title_en: suggestion.title_en || '', title_fi: suggestion.title_fi || '',
            description: suggestion.description_en || '', description_en: suggestion.description_en || '', description_fi: suggestion.description_fi || '',
            price: suggestion.price || 1, barcode, status: 'for_sale', reservation_id: reservationId,
          }).eq('id', productId)
          setProducts((prev) => prev.map((p) => p.id === productId ? { ...p, status: 'success', title: language === 'fi' ? suggestion.title_fi : suggestion.title_en, price: suggestion.price || 1 } : p))
        } catch (aiError) {
          console.error('AI labeling error:', aiError)
          await supabase.from('products').update({ status: 'for_sale', reservation_id: reservationId }).eq('id', productId)
          setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, status: 'success' } : p)))
        }
      } else {
        await supabase.from('products').update({ status: 'for_sale', reservation_id: reservationId }).eq('id', productId)
        setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, status: 'success' } : p)))
      }
    } catch (error: any) {
      console.error('Error uploading product:', error)
      setProducts((prev) => prev.map((p) => p.id === localId ? { ...p, status: 'error', error: error.message } : p))
    }
  }

  if (loading) return <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>

  const uploadingCount = products.filter((p) => p.status === 'uploading').length
  const analyzingCount = products.filter((p) => p.status === 'analyzing').length
  const successCount = products.filter((p) => p.status === 'success').length
  const errorCount = products.filter((p) => p.status === 'error').length

  return (
    <FullScreenLayout>
      {/* Header */}
      <XStack ai="center" px="$md" pt="$md" pb="$sm" bg="$backgroundCard" bbw={1} bbc="$borderColor">
        <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
        <YStack f={1} ml="$sm">
          <Text fontSize={20} fontWeight="bold" color="$color">{t('bulk_upload') || 'Bulk Upload'}</Text>
          {reservation && <Text fontSize={14} color="$colorSecondary" mt={2}>{reservation.space_name} @ {reservation.market_name}</Text>}
        </YStack>
        <XStack ai="center" bg="$backgroundSecondary" px={10} py={6} br="$full" gap={6}>
          <Text fontSize={12} fontWeight="600" color="$colorSecondary">{t('ai_label') || 'AI'}</Text>
          <Switch value={autoLabel} onValueChange={toggleAutoLabel} trackColor={{ false: '#767577', true: '#9C27B0' }} thumbColor={autoLabel ? '#E1BEE7' : '#f4f3f4'} />
        </XStack>
      </XStack>

      {/* Status Bar */}
      <XStack ai="center" jc="space-between" px="$md" py="$sm" bg="$backgroundCard" bbw={1} bbc="$borderColor">
        <Button variant="primary" onPress={pickImages}><ButtonText>+ {t('add_images') || 'Add Images'}</ButtonText></Button>
        <XStack gap="$sm">
          {uploadingCount > 0 && <XStack ai="center" px={10} py={6} br="$full" gap={4} bg="#FF9800"><Spinner size="small" /><Text color="white" fontSize={12}>{uploadingCount}</Text></XStack>}
          {analyzingCount > 0 && <XStack ai="center" px={10} py={6} br="$full" gap={4} bg="#9C27B0"><Text color="white" fontSize={14} fontWeight="bold">AI</Text><Text color="white" fontSize={12}>{analyzingCount}</Text></XStack>}
          {successCount > 0 && <XStack ai="center" px={10} py={6} br="$full" gap={4} bg="#4CAF50"><Check size={14} color="#fff" /><Text color="white" fontSize={12}>{successCount}</Text></XStack>}
          {errorCount > 0 && <XStack ai="center" px={10} py={6} br="$full" gap={4} bg="#F44336"><Text color="white" fontSize={14} fontWeight="bold">✗</Text><Text color="white" fontSize={12}>{errorCount}</Text></XStack>}
        </XStack>
      </XStack>

      {/* Products Grid */}
      <ScrollView f={1} contentContainerStyle={{ padding: 16, minHeight: 200 }}>
        {products.length === 0 ? (
          <YStack f={1} jc="center" ai="center" py="$3xl">
            <YStack w={80} h={80} br="$full" bg="rgba(156, 39, 176, 0.15)" jc="center" ai="center" mb="$md">
              <Camera size={48} color="#9C27B0" />
            </YStack>
            <Text fontSize={18} color="$colorSecondary" mb="$sm">{t('no_images_selected') || 'No images selected'}</Text>
            <Text fontSize={14} color="#999">{t('tap_add_images') || 'Tap "Add Images" to select photos'}</Text>
          </YStack>
        ) : (
          <XStack flexWrap="wrap" gap="$sm">
            {products.map((product) => (
              <YStack key={product.id} w={isDesktop ? 160 : (width - 48) / 3} bg="$backgroundCard" br="$lg" p="$sm" elevation={2}>
                <ZStack mb={6}>
                  <Image source={{ uri: product.localUri }} style={{ width: '100%', aspectRatio: 1, borderRadius: 8, backgroundColor: '#f0f0f0' }} />
                  {product.status === 'uploading' && (
                    <YStack {...{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} bg="rgba(0,0,0,0.6)" br="$md" jc="center" ai="center">
                      <Spinner color="#fff" /><Text color="white" fontSize={10} mt={4}>{t('uploading') || 'Uploading'}</Text>
                    </YStack>
                  )}
                  {product.status === 'analyzing' && (
                    <YStack {...{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} bg="rgba(156, 39, 176, 0.3)" br="$md" jc="center" ai="center">
                      <Spinner color="#9C27B0" /><Text color="#9C27B0" fontSize={12} fontWeight="bold" mt={4}>AI</Text>
                    </YStack>
                  )}
                  {product.status === 'success' && (
                    <YStack position="absolute" top={4} right={4} w={24} h={24} br="$full" bg="#4CAF50" jc="center" ai="center"><Check size={12} color="#fff" /></YStack>
                  )}
                  {product.status === 'error' && (
                    <YStack position="absolute" top={4} right={4} w={24} h={24} br="$full" bg="#F44336" jc="center" ai="center"><Text color="white" fontWeight="bold" fontSize={14}>!</Text></YStack>
                  )}
                </ZStack>
                {product.title && <Text fontSize={11} color="$color" numberOfLines={2} mb={2}>{product.title}</Text>}
                {product.price && <Text fontSize={12} fontWeight="600" color="$colorPrimary">{product.price.toFixed(2)} €</Text>}
              </YStack>
            ))}
          </XStack>
        )}
      </ScrollView>
    </FullScreenLayout>
  )
}
